
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Grid,
    Icon,
    Snackbar,
    Alert,
} from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import DataTable from "examples/Tables/DataTable";
import TableSkeleton from 'components/CustomSkeleton/TableSkeleton';
import EmptyState from 'components/EmptyTable/EmptyState';
import { useAuth } from 'auth/Auth';
import { usePermission } from 'auth/usePermission';
import NewMerchantAssetInventoryModal from 'components/NewMerchantAssetInventoryModal';
import StatusBadge from "components/StatusBadge";
import MonoText from 'components/MonoText';
import { formatDate } from 'utils/DateTimeConversion';

const MerchantAssetInventories = () => {
    const [inventories, setInventories] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
    const { axiosInstance } = useAuth();
    const navigate = useNavigate();
    const { canCreate } = usePermission('merchant_asset_inventories');

    useEffect(() => {
        fetchInventories();
    }, []);

    const fetchInventories = async () => {
        setIsLoading(true);
        try {
            const response = await axiosInstance.get('/merchant-asset-inventories/', {
                params: { limit: 200 },
            });
            setInventories(response.data || []);
        } catch (error) {
            console.error('Error fetching merchant asset inventories:', error);
            setSnackbar({ open: true, message: 'Error loading merchant asset inventories', severity: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = () => setIsModalOpen(true);
    const handleCloseModal = () => setIsModalOpen(false);

    const handleSaveInventory = async (payload) => {
        try {
            await axiosInstance.post('/merchant-asset-inventories/', payload);
            setSnackbar({ open: true, message: 'Merchant Asset Inventory created successfully', severity: 'success' });
            fetchInventories();
        } catch (error) {
            console.error('Error saving merchant asset inventory:', error);
            throw error;
        }
    };

    const handleRowClick = (rowData) => {
        navigate(`/merchant-asset-inventory/${rowData.id}`);
    };

    const columns = [
        {
            Header: "Inventory ID",
            accessor: "id",
            width: "12%",
            Cell: ({ value, row }) => (
                <MDTypography
                    component="span"
                    onClick={() => handleRowClick(row.original)}
                    variant="caption"
                    color="info"
                    fontWeight="medium"
                    sx={{ cursor: 'pointer' }}
                >
                    #{value}
                </MDTypography>
            )
        },
        {
            Header: "Asset",
            accessor: "merchant_asset_name",
            width: "22%",
            Cell: ({ row }) => {
                const name = row.original.merchant_asset_name;
                const assetId = row.original.merchant_asset_id;
                return (
                    <MDTypography
                        component="span"
                        variant="caption"
                        color="info"
                        fontWeight="medium"
                        sx={{ cursor: assetId ? 'pointer' : 'default' }}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (assetId) navigate(`/merchant-assets/${assetId}`);
                        }}
                    >
                        {name || (assetId ? `Asset #${assetId}` : '-')}
                    </MDTypography>
                );
            }
        },
        {
            Header: "Departure Date",
            accessor: "date",
            width: "14%",
            Cell: ({ value }) => value ? formatDate(value, 'date') : '-'
        },
        {
            Header: "Status",
            accessor: "status",
            width: "12%",
            Cell: ({ value, row }) => {
                const status = value || (row.original.blackout ? 'Unavailable' : 'Available');
                return <StatusBadge status={status} size="xs" />;
            }
        },
        {
            Header: "Created",
            accessor: "created_at",
            width: "18%",
            Cell: ({ value }) => (
                <MonoText>{value ? formatDate(value, 'datetime') : '-'}</MonoText>
            ),
        },
        {
            Header: "Modified",
            accessor: "updated_at",
            width: "18%",
            Cell: ({ value, row }) => (
                <MonoText>
                    {formatDate(value || row.original.created_at, 'datetime') || '-'}
                </MonoText>
            ),
        },
    ];

    const tableData = useMemo(() => ({
        columns,
        rows: inventories,
    }), [inventories]);

    return (
        <DashboardLayout>
            <DashboardNavbar />
            <MDBox pt={6} pb={3}>
                <Grid container spacing={6}>
                    <Grid item xs={12}>
                        <Card>
                            <MDBox
                                mx={2}
                                mt={-3}
                                py={3}
                                px={2}
                                variant="gradient"
                                bgColor="info"
                                borderRadius="lg"
                                coloredShadow="info"
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <MDTypography variant="h6" color="white">Merchant Asset Inventory</MDTypography>
                                <MDButton
                                    variant="gradient"
                                    color="dark"
                                    onClick={handleOpenModal}
                                    disabled={!canCreate}
                                >
                                    <Icon sx={{ fontWeight: "bold" }}>add</Icon>
                                    &nbsp;Add Inventory
                                </MDButton>
                            </MDBox>
                            <MDBox pt={3}>
                                {isLoading ? (
                                    <TableSkeleton />
                                ) : inventories.length > 0 ? (
                                    <DataTable
                                        table={tableData}
                                        isSorted={true}
                                        entriesPerPage={{ defaultValue: 10, entries: [5, 10, 15, 20, 25] }}
                                        canSearch
                                        showTotalEntries
                                        noEndBorder
                                    />
                                ) : (
                                    <EmptyState moduleName="Merchant Asset Inventory" canCreate={canCreate} />
                                )}
                            </MDBox>
                        </Card>
                    </Grid>
                </Grid>
            </MDBox>

            <NewMerchantAssetInventoryModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSave={handleSaveInventory}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </DashboardLayout>
    );
};

export default MerchantAssetInventories;
